import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { GlobalToken, message, Popconfirm, Popover } from 'antd';
import moment from 'moment';
import { floor, toUpper } from 'lodash';
import { observer } from 'mobx-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { EPTable } from '@/comp';
import { useStores } from '@/hooks/use-stores';
import { AlertType } from '@/constants/alertType.enum';
import IconConfigure from '@/assets/icon-configure.svg?react';
import { AlertEntity } from '@/comp/AlertsBar/Alert';
import { EPButton } from '@/components';
import ArchiveIcon from '@/comp/common/icons/ArchiveIcon';
import UnarchiveIcon from '@/comp/common/icons/UnarchiveIcon';
import Severity from '../../Rules/Severity';

moment.updateLocale('en', {
  calendar: {
    lastDay: '[Yesterday,] h:mma',
    sameDay: '[Today,] h:mma',
    sameElse: 'MMM D[,] Y[,] h:mma',
  },
});

type AlertTableRecord = {
  key: string;
  id: string;
  alertRecord: AlertEntity;
  status: string;
  severity: string;
  source: AlertType;
  dateFired: Date;
  deviceNames: string;
};

const AlertsTable = observer(({ alerts, loading }) => {
  const { t } = useTranslation();
  const [data, setData] = useState<AlertTableRecord[] | null>();
  const [numOfAlerts, setNumOfAlerts] = useState(0);

  const columns = [
    {
      title: <p className={'bold-500'}>{t('Alert Record')}</p>,
      dataIndex: 'alertRecord',
      render: alert => {
        return <AlertRecordInfo alert={alert} />;
      },
    },
    {
      title: <p className={'bold-500'}>{t('Devices Names')}</p>,
      dataIndex: 'deviceNames',
      sorter: (a, b) => {
        return a.deviceNames > b.deviceNames ? -1 : 1;
      },
    },
    {
      title: <p className={'bold-500'}>{t('Status')}</p>,
      dataIndex: 'status',
      render: alert => {
        return <RecordStatus alert={alert} />;
      },
    },
    {
      title: <p className={'bold-500'}>{t('Severity')}</p>,
      dataIndex: 'severity',
      width: 110,
      render: severity => (
        <div className="severity-holder">
          <div className="spacer" />
          <Severity alertRule={{ severity }} />
        </div>
      ),
    },
    {
      title: <p className={'bold-500'}>{t('Source')}</p>,
      dataIndex: 'source',
    },
    {
      title: <p className={'bold-500'}>{t('Date Fired')}</p>,
      dataIndex: 'dateFired',
      sorter: (a, b) => {
        const timestampA = moment(a.dateFired).valueOf();
        const timestampB = moment(b.dateFired).valueOf();

        if (timestampA < timestampB) return -1;
        if (timestampA > timestampB) return 1;
        return 0;
      },
      render: value => (value ? moment(value).calendar() : ''),
    },
    {
      title: '',
      key: 'buttons',
      dataIndex: 'buttons',
      render: ({ alert }) => {
        return (
          <div className="alert-buttons">
            <ArchiveButton alert={alert} />
          </div>
        );
      },
    },
  ];

  const dataSource = (): AlertTableRecord[] => {
    return alerts?.map(alert => {
      return {
        key: alert.id,
        id: alert.id,
        alertRecord: alert,
        status: alert,
        severity: alert.severity,
        source: alert.alertType,
        dateFired: alert.openingTime,
        buttons: { alert: alert, acknowledged: alert.acknowledged },
        deviceNames: alert.affectedDevicesNames?.toString(),
      };
    });
  };

  useEffect(() => {
    setData(dataSource());
    setNumOfAlerts(alerts?.length);
  }, [alerts]);

  return (
    <Container>
      <div className="displayed-alerts">{`${t('Displayed alerts')} ${numOfAlerts}`}</div>
      <EPTable
        dataSource={data}
        columns={columns}
        loading={loading}
        loadingRowCount={2}
        locale={{ emptyText: t('No data') }}
        pagination={false}
      />
    </Container>
  );
});

const Container = styled.div<{ theme: { token: GlobalToken } }>`
  height: 100%;
  width: 100%;

  .displayed-alerts {
    font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
    color: ${({ theme: { token } }) => token.colorBorder};
  }

  .ant-table {
    font-size: ${({ theme: { token } }) => token.fontSize}px;
    background: ${({ theme: { token } }) => token.colorBgLayout};

    .ant-table-thead > tr > th {
      background: ${({ theme: { token } }) => token.colorBgLayout};
    }

    .ant-table-thead > tr > td {
      background: ${({ theme: { token } }) => token.colorBgLayout};
      border-bottom: 2px solid ${({ theme: { token } }) => token.colorBorder};
    }

    .ant-table-column-sorter {
      color: ${({ theme: { token } }) => token.colorBgLayout};
    }

    .ant-table-tbody > tr > td.ant-table-cell-row-hover {
      background: ${({ theme: { token } }) => token.colorBgLayout};
    }

    .ant-table-tbody > tr > td:first-child {
      padding-left: 0;
    }

    .ant-table-thead > tr > th {
      font-size: ${({ theme: { token } }) => token.fontSizeLG}px;

      &:first-child {
        padding-left: 0;
        width: 35%;
      }

      &:last-child {
        width: 5%;
      }
    }

    .alert-record-content {
      .alert-archived {
        display: inline-block;
        padding: 0 ${({ theme: { token } }) => token.paddingXXS}px;
        background-color: ${({ theme: { token } }) => token.colorBgContainer};
        font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
      }

      .alert-title {
        font-size: ${({ theme: { token } }) => token.fontSizeLG}px;
        font-weight: ${({ theme: { token } }) => token.fontWeightStrong};
      }

      .time-period {
        color: ${({ theme: { token } }) => token.colorTextDisabled};
        font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
      }

      .alert-description {
        padding-top: ${({ theme: { token } }) => token.paddingSM}px;
        color: ${({ theme: { token } }) => token.colorTextDisabled};
        font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
      }
    }

    .status-holder {
      display: flex;
      align-items: center;

      .green-square {
        height: 8px;
        width: 8px;
        border-radius: 1.3px;
        background-color: ${({ theme: { token } }) => token.colorSuccess};
      }

      .red-square {
        height: 8px;
        width: 8px;
        border-radius: 1.3px;
        background-color: ${({ theme: { token } }) => token.colorError};
      }

      .status-text {
        font-size: ${({ theme: { token } }) => token.fontSize}px;
        padding: ${({ theme: { token } }) => `${token.paddingXXS}px ${token.paddingXS}px`};
      }
    }

    .alert-buttons {
      display: flex;
      justify-content: space-between;
      width: 60px;

      .ant-btn {
        .archive-icon {
          padding-top: ${({ theme: { token } }) => token.paddingXXS}px;
        }
      }
    }

    .severity-holder {
      .rule-item-severity {
        display: flex;
        font-size: ${({ theme: { token } }) => token.fontSizeSM}px;
        justify-items: center;
        border-radius: 1.3px;
        height: 20px;

        .severity-label {
          padding: 0 ${({ theme: { token } }) => token.paddingXXS}px;
          background-color: ${({ theme: { token } }) => token.colorBgContainer};
        }

        .severity-icon {
          svg {
            height: 20px;
            width: 20px;
          }
        }
      }
    }
  }

  .ant-table:not(.ant-table-bordered) .ant-table-tbody > tr.ant-table-row:hover > td,
  .ant-table:not(.ant-table-bordered) .ant-table-tbody > tr.ant-table-row:hover + tr.ant-table-row > td {
    border-top-color: ${({ theme: { token } }) => token.colorBorder};
    border-bottom-color: ${({ theme: { token } }) => token.colorBorder};
  }

  .ant-table:not(.ant-table-bordered) .ant-table-tbody > tr > td,
  .ant-table:not(.ant-table-bordered) .ant-table-tbody > tr:last-child > td,
  .ant-table:not(.ant-table-bordered) .ant-table-tbody > tr.ant-table-row:last-child:hover > td {
    border-top-color: ${({ theme: { token } }) => token.colorBorder};
    border-bottom-color: ${({ theme: { token } }) => token.colorBorder};
  }
`;

const AlertRecordInfo = observer(({ alert }) => {
  const { t } = useTranslation();

  const getTimePeriod = () => {
    const periodInMinutes = moment().diff(moment(alert.openingTime), 'minutes');
    const minutes = `${periodInMinutes % 60}m`;
    const hours = floor(periodInMinutes / 60) > 0 ? `${floor(periodInMinutes / 60) % 24}h` : '';
    const days = floor(periodInMinutes / 1440) > 0 ? `${floor(periodInMinutes / 1440)}d` : '';

    return `${days} ${hours} ${minutes}`;
  };

  return (
    <div className="alert-record-content">
      {alert.acknowledged && <div className="alert-archived">{t('Archived')}</div>}
      <div className="alert-title">{alert.title}</div>
      <div className="time-period">{`${t('Time period')}: ${
        toUpper(alert.status) === 'OPEN' ? getTimePeriod() : t('N/A')
      }`}</div>
      <div className="alert-description">{alert.detailedDescription}</div>
    </div>
  );
});

const RecordStatus = observer(({ alert }) => {
  const { t } = useTranslation();

  const getStatus = () => {
    switch (toUpper(alert.status)) {
      case 'OPEN':
        return (
          <div className="status-holder">
            <div className="green-square" />
            <div className="status-text">{t('Open')}</div>
          </div>
        );
      case 'CLOSE':
        return (
          <div className="status-holder">
            <div className="red-square" />
            <div className="status-text">{t('Close')}</div>
          </div>
        );
      default:
        return null;
    }
  };
  return getStatus();
});

const ArchiveButton = observer(({ alert }) => {
  const { t } = useTranslation();
  const { alertStore } = useStores();
  const params = useParams();
  const navigate = useNavigate();

  const onClickArchive = async () => {
    await alertStore.archive(alert.id);
    alert.acknowledged = true;
  };

  const onClickUnArchive = async () => {
    try {
      await alertStore.unArchive(alert.id);
      alert.acknowledged = false;
    } catch {
      message.error(t('Unarchive alert failed'));
    }
  };

  const onClickRuleIcon = () => {
    navigate(`/home/station/${params.stationId}/rules/${alert.alertType}/${alert.alertRuleId}/${alert.id}/view`, {
      state: { previous: 'tab/investigation' },
    });
  };
  return (
    <IconsContainer>
      <Popover placement="bottom" content={<span>{t(`View Alert record's rule (logic)`)}</span>}>
        <EPButton
          type="link"
          size="small"
          icon={<IconConfigure className="icon icon-configure-specific-rule" />}
          disabled={false}
          onClick={onClickRuleIcon}
        />
      </Popover>
      {alert.acknowledged ? (
        <Popover placement="bottom" content={<span>{t('Unarchive Alert')}</span>}>
          <Popconfirm
            placement="bottom"
            title={t('Are you sure you want to unarchive this alert record?')}
            onConfirm={onClickUnArchive}
            okText={t('Yes')}
            cancelText={t('cancel')}
            icon={null}
          >
            <EPButton type="link" size="small" className="archive-icon" icon={<UnarchiveIcon />} />
          </Popconfirm>
        </Popover>
      ) : (
        <Popover placement="bottom" content={<span>{t('Archive Alert')}</span>}>
          <EPButton type="link" size="small" className="archive-icon" icon={<ArchiveIcon />} onClick={onClickArchive} />
        </Popover>
      )}
    </IconsContainer>
  );
});

const IconsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  width: 60px;

  .archive-icon {
    padding-top: ${({ theme: { token } }) => token.paddingXXS}px;
  }
`;
export default AlertsTable;
